import moment from 'moment-timezone';

import { ISchedule } from '../schedule';
import { ISession, SingleCommunication } from '../session';

export type Mode = 'D' | 'T' | 'O' | 'W' | 'C';

export type PHQMode = 'none' | 'phq9' | 'phq2_to_phq9';

export interface IEpxDepressSchedule extends ISchedule {
  mode?: Mode;
  hasPHQ?: boolean;
  isPHQScreening?: boolean;
  hasGranularPHQ9Alerts?: boolean;
  hasGAD?: boolean;
  hasCOVID?: boolean;
  serial?: number;
  phqFrequency?: number;
  gadFrequency?: number;
  suicide?: boolean;
  suicideContact?: boolean;
  supportPhone?: string;
  supportName?: string;
  variant?: string;
}

export interface IEpxDepress
  extends ISession<IEpxDepressSchedule>, SingleCommunication {
  mood?: number;
  sleep?: number;
  suicide?: boolean;
  suicideTrend?: boolean;
  isPsudo?: boolean;
  suicideConnect?: boolean;
  variant?: string;
  _phqs?: IEpxDepressPHQ;
  _gads?: IEpxDepressGAD;
}

export interface IEpxDepressPHQ extends ISession<IEpxDepressSchedule> {
  serial?: number;
  isScreening?: boolean;
  answers?: {
    q1?: number;
    q2?: number;
    q3?: number;
    q4?: number;
    q5?: number;
    q6?: number;
    q7?: number;
    q8?: number;
    q9?: number;
  };
}

export interface IEpxDepressGAD extends ISession<IEpxDepressSchedule> {
  serial?: number;
  q1?: number;
  q2?: number;
  q3?: number;
  q4?: number;
  q5?: number;
  q6?: number;
  q7?: number;
  impact?: number;
  total?: number;
}

export interface IEpxDepressCOVID extends ISession<IEpxDepressSchedule> {
  phq4Q1?: number;
  phq4Q2?: number;
  phq4Q3?: number;
  phq4Q4?: number;
  phq4Total?: number;
  isWorsenedAfterActivity?: boolean;
}

export function ProcessSchedule<
  T extends {
    times: string[];
  }
>(data: T, schedule: IEpxDepressSchedule): T {
  const start = moment.tz(schedule.start, schedule.tz);
  const times = [start.format('HH:mm')];
  data.times = times;
  return data;
}

export function getPHQAnswer(
  session: IEpxDepressPHQ,
  index: number
): {
  value: number | null;
  invalidated: boolean;
  invalidationDate?: string;
  prevValue?: number | string;
} {
  const invalidation =
    session.invalidation && session.invalidation[index.toString()]
      ? session.invalidation[index.toString()]
      : null;

  if (invalidation) {
    const raw = invalidation?.newValue;
    const invalidationDate = invalidation?.createdAt
      ? moment(invalidation.createdAt).format('MMM D, YYYY h:mm A')
      : undefined;
    const prevValue = invalidation?.previousValue;
    return {
      value: raw != null ? parseInt(raw as string, 10) : null,
      invalidated: true,
      invalidationDate,
      prevValue
    };
  }
  const { answers: rawAnswers } = session;
  let value: number | null = null;
  if (!rawAnswers) {
    value = null;
  } else if (typeof rawAnswers === 'string') {
    value = JSON.parse(rawAnswers)[index] ?? null;
  } else if (typeof rawAnswers === 'object') {
    value = (rawAnswers as Record<string, number>)[index] ?? null;
  }
  return {
    value,
    invalidated: false
  };
}

export function getPHQ2Total(session: IEpxDepressPHQ): number | null {
  const q1 = getPHQAnswer(session, 1);
  const q2 = getPHQAnswer(session, 2);
  if (q1.value == null || q2.value == null) {
    return null;
  }
  return q1.value + q2.value;
}

export function isPHQ2Escalated(session: IEpxDepressPHQ): boolean {
  const total = getPHQ2Total(session);
  return total != null && total >= 3;
}

export function phqModeFromSchedule(schedule: IEpxDepressSchedule): PHQMode {
  if (!schedule.hasPHQ) return 'none';
  if (schedule.isPHQScreening) return 'phq2_to_phq9';
  return 'phq9';
}

export function scheduleFromPHQMode(mode: PHQMode): {
  hasPHQ: boolean;
  isPHQScreening: boolean;
} {
  switch (mode) {
    case 'none':
      return { hasPHQ: false, isPHQScreening: false };
    case 'phq9':
      return { hasPHQ: true, isPHQScreening: false };
    case 'phq2_to_phq9':
      return { hasPHQ: true, isPHQScreening: true };
    default:
      // Safe fallback for unexpected values to avoid returning undefined
      return { hasPHQ: false, isPHQScreening: false };
  }
}

export function getGADAnswer(
  session: IEpxDepressGAD,
  index: number
): {
  value: number | null;
  invalidated: boolean;
  invalidationDate?: string;
  prevValue?: number | string;
} {
  const keyName = `q${index}`;
  const invalidation =
    session.invalidation && session.invalidation[keyName]
      ? session.invalidation[keyName]
      : null;
  if (invalidation) {
    const raw = invalidation?.newValue;
    const invalidationDate = invalidation?.createdAt
      ? moment(invalidation.createdAt).format('MMM D, YYYY h:mm A')
      : undefined;
    const prevValue = invalidation?.previousValue;
    return {
      value: raw != null ? parseInt(raw as string, 10) : null,
      invalidated: true,
      invalidationDate,
      prevValue
    };
  }
  const value = session[keyName] as unknown as number | null;
  return {
    value,
    invalidated: false
  };
}
